import JSZip from 'jszip';
import { enqueueSnackbar } from 'notistack';
import { useState } from 'react';
import { useRecoilValue } from 'recoil';
import {
    UploadDICOMRequestBody,
    useUploadDICOM,
} from '~/backend/connector-api/connector-components';
import { Roi } from '~/domain/dicom/dicomstructureset';
import { DoseMatrix } from '~/domain/patient/doseMatrix';
import { Image3D } from '~/domain/patient/image3d';
import { StructureSet } from '~/domain/patient/structureset';
import { environmentState } from '~/state/atoms/engineAtoms';
import { handleError } from '~/utils/errors';
import {
    downloadImageToDisk,
    saveBlobToFile,
} from '~/utils/io/files/downloadDicom';
import useSelectedPatient from '../patient/useSelectedPatient';

type SelectedStructureSetsWithStructures = {
    structureSet: StructureSet;
    selectedRois?: Roi[];
};

/**
 * Extracts the DICOM dose and plan blobs from a DoseMatrix.
 */
const getDoseBlobs = (
    dose: DoseMatrix
): { doseBlob?: Blob; planBlob?: Blob } => ({
    doseBlob: dose.dicomDose.getAsDicomBlob(),
    planBlob: dose.plan?.dicom?.getAsDicomBlob(),
});

export const useDicomExport = () => {
    const { mutateAsync: uploadDICOM } = useUploadDICOM();
    const selectedPatient = useSelectedPatient();
    const [exporting, setExporting] = useState(false);
    const environment = useRecoilValue(environmentState);

    /**
     * Extracts selected structures from a StructureSet as a DICOM Blob.
     */
    const structureSetToBlob = (
        structureSet: StructureSet,
        selectedRois?: Roi[],
        exportToMonaco?: boolean
    ): Blob => {
        if (!environment) {
            throw new Error('Internal error, Environment not found');
        }
        environment.saveChanges();
        if (selectedRois) {
            return structureSet.exportWith(
                selectedRois.map((roi) => roi.roiNumber),
                exportToMonaco ? 'ISO_IR 100' : undefined
            );
        }
        return structureSet.export();
    };

    /**
     * Converts a StructureSet to a Monaco DIR file Blob.
     *
     * This function exports the structure set in a format compatible with Monaco,
     * allowing for easy sharing and collaboration.
     *
     * @param {StructureSet} structureSet - The structure set to be exported.
     * @returns {Blob} A Blob representing the exported structure set in Monaco format.
     */
    const monacoStructureSetToBlob = (structureSet: StructureSet): Blob => {
        if (!environment) {
            throw new Error('Internal error, Environment not found');
        }
        environment.saveChanges();
        return structureSet.exportAsMonacoFile(
            selectedPatient?.dataSet.PatientName ?? 'NO_NAME'
        );
    };

    const uploadDicomBlob = async (
        blob: Blob,
        filename: string
    ): Promise<void> => {
        const formData = new FormData();
        formData.append('files', blob, filename);
        await uploadDICOM({
            body: formData as unknown as UploadDICOMRequestBody,
            pathParams: { target: 'default' },
            headers: { 'Content-Type': 'multipart/form-data' },
        });
    };

    /**
     * Downloads a DICOM Image as a ZIP file.
     *
     * This function extracts all DICOM slices from an Image3D object,
     * combines them into a ZIP archive, and downloads it.
     *
     * @param {Image3D} image - The image to be downloaded.
     * @returns {Promise<void>} A promise that resolves when the download completes.
     */
    const handleDownloadImage = async (image: Image3D): Promise<void> => {
        setExporting(true);
        await downloadImageToDisk(image);
        setExporting(false);
    };

    /**
     * Exports a DICOM Image by uploading all slices.
     *
     * This function extracts all DICOM slices from an Image3D object,
     * and uploads them individually to the server.
     *
     * @param {Image3D} image - The image to be exported.
     * @returns {Promise<void>} A promise that resolves when the upload completes.
     */
    const handleExportImage = async (image: Image3D): Promise<void> => {
        try {
            setExporting(true);

            const blobs = Array.from(image.getSlicesAsDicomBlobs());

            for (let i = 0; i < blobs.length; i++) {
                const blob = blobs[i];
                await uploadDicomBlob(
                    blob,
                    `${image.seriesMetadata.modality}_${image.dicomUid}_${i}.dcm`
                );
            }

            image.setExported();

            enqueueSnackbar('DICOM image files uploaded successfully', {
                variant: 'success',
            });
        } catch (error) {
            console.error('Error during image export:', error);
            enqueueSnackbar('Failed to upload image files', {
                variant: 'error',
            });
        } finally {
            setExporting(false);
        }
    };

    /**
     * Downloads a DICOM Structure Set file.
     *
     * This function exports a Structure Set either with a subset of selected ROIs
     * or the entire structure set, then saves it as a `.dcm` file.
     *
     * @param {StructureSet} structureSet - The structure set to be exported.
     * @param {Roi[]} [selectedRois] - An optional array of ROIs to include in the export.
     *                                  If omitted, the entire structure set is exported.
     * @returns {Promise<void>} A promise that resolves when the export process completes.
     */
    const handleDownloadStructureSet = async (
        structureSet: StructureSet,
        selectedRois?: Roi[]
    ): Promise<void> => {
        try {
            setExporting(true);

            const structureSetBlob = structureSetToBlob(
                structureSet,
                selectedRois
            );

            if (!structureSetBlob)
                throw new Error('Failed to generate Structure Set Blob');

            saveBlobToFile(
                structureSetBlob,
                `RS_${structureSet.structureSetLabel}.dcm`
            );
        } catch (error) {
            handleError(error, 'Exporting files failed, please try again.');
        } finally {
            setExporting(false);
        }
    };

    /**
     * Exports a Structure Set as a DICOM file and uploads it.
     *
     * This function converts a selected subset of ROIs from the structure set into a DICOM file,
     * packages it as FormData, and uploads it to the server.
     *
     * @param {StructureSet} structureSet - The structure set containing the ROIs.
     * @param {Roi[]} selectedRois - The list of ROIs to include in the export.
     * @returns {Promise<void>} A promise that resolves when the upload completes.
     */
    const handleExportStructureSet = async (
        structureSet: StructureSet,
        selectedRois: Roi[]
    ): Promise<void> => {
        try {
            setExporting(true);

            const structureSetBlob = structureSetToBlob(
                structureSet,
                selectedRois
            );

            if (!structureSetBlob)
                throw new Error('Failed to generate Structure Set Blob');

            const formData = new FormData();
            formData.append(
                'files',
                structureSetBlob,
                `RS_${structureSet.structureSetLabel}.dcm`
            );

            await uploadDICOM({
                body: formData as unknown as UploadDICOMRequestBody,
                pathParams: { target: 'default' },
                headers: { 'Content-Type': 'multipart/form-data' },
            });

            structureSet.setExported();

            enqueueSnackbar('DICOM files uploaded successfully', {
                variant: 'success',
            });
        } catch (error) {
            console.error('Error during export:', error);
            enqueueSnackbar('Failed to upload DICOM files', {
                variant: 'error',
            });
        } finally {
            setExporting(false);
        }
    };

    /**
     * Exports a Dose Matrix as DICOM files and uploads them.
     *
     * This function extracts the DICOM dose and plan from a Dose Matrix,
     * packages them as FormData, and uploads them to the server.
     *
     * @param {DoseMatrix} dose - The dose matrix to be exported.
     * @returns {Promise<void>} A promise that resolves when the upload completes.
     */
    const handleExportDose = async (dose: DoseMatrix): Promise<void> => {
        try {
            setExporting(true);
            const { doseBlob, planBlob } = getDoseBlobs(dose);
            if (planBlob) {
                await uploadDicomBlob(
                    planBlob,
                    `RTPLAN_${dose.plan?.label ?? dose.dicomUid}.dcm`
                );
            }
            if (doseBlob) {
                await uploadDicomBlob(
                    doseBlob,
                    `RTDOSE_${dose.plan?.label ?? dose.dicomUid}.dcm`
                );
            }

            dose.setExported();
            dose.plan?.setExported();

            enqueueSnackbar('DICOM files uploaded successfully', {
                variant: 'success',
            });
        } catch (error) {
            console.error('Error during export:', error);
            enqueueSnackbar('Failed to upload DICOM files', {
                variant: 'error',
            });
        } finally {
            setExporting(false);
        }
    };

    /**
     * Downloads a Dose Matrix as DICOM files.
     *
     * This function extracts the DICOM dose and plan from a Dose Matrix
     * and saves them as `.dcm` files.
     *
     * @param {DoseMatrix} dose - The dose matrix to be downloaded.
     * @returns {Promise<void>} A promise that resolves when the download completes.
     */
    const handleDownloadDose = async (dose: DoseMatrix): Promise<void> => {
        try {
            setExporting(true);
            const { doseBlob, planBlob } = getDoseBlobs(dose);
            if (doseBlob) {
                saveBlobToFile(
                    doseBlob,
                    `RTDOSE_${dose.plan?.label ?? dose.dicomUid}.dcm`
                );
            }
            if (planBlob) {
                saveBlobToFile(
                    planBlob,
                    `RTPLAN_${dose.plan?.label ?? dose.dicomUid}.dcm`
                );
            }
        } catch (error) {
            handleError(
                error,
                'Downloading dose files failed, please try again.'
            );
        } finally {
            setExporting(false);
        }
    };

    /**
     * Downloads multiple selected `StructureSet`, `DoseMatrix`, and `Image3D` as a batch.
     *
     * If there's only one item, it will download it directly.
     * If there are multiple items, it will combine them into a ZIP archive.
     *
     * @param {SelectedStructureSetsWithStructures[]} selectedStructureSets -
     *        An array of objects containing a StructureSet and its selected ROIs.
     * @param {DoseMatrix[]} selectedDoses -
     *        An array of DoseMatrix objects representing dose distributions to be downloaded.
     * @param {Image3D[]} selectedImages -
     *        An array of Image3D objects representing images to be downloaded.
     * @returns {Promise<void>} A promise that resolves when all downloads are completed.
     */
    const handleDownloadBatch = async (
        selectedImages: Image3D[] = [],
        selectedStructureSets: SelectedStructureSetsWithStructures[],
        selectedDoses: DoseMatrix[],
        exportToMonaco: boolean = false
    ): Promise<void> => {
        try {
            setExporting(true);
            const totalItems =
                selectedStructureSets.length +
                selectedDoses.length +
                selectedImages.length;

            if (totalItems === 1) {
                if (selectedImages.length === 1) {
                    return await handleDownloadImage(selectedImages[0]);
                } else if (
                    selectedStructureSets.length === 1 &&
                    !exportToMonaco
                ) {
                    const { structureSet, selectedRois } =
                        selectedStructureSets[0];
                    return await handleDownloadStructureSet(
                        structureSet,
                        selectedRois
                    );
                } else if (selectedDoses.length === 1) {
                    return await handleDownloadDose(selectedDoses[0]);
                }
            }

            const timestamp = new Date()
                .toISOString()
                .replace(/[:.]/g, '-')
                .slice(0, -5);
            const filename = `dicom-export-${timestamp}`;

            const zip = new JSZip();

            for (const image of selectedImages) {
                const blobs = Array.from(image.getSlicesAsDicomBlobs());
                blobs.forEach((blob, index) => {
                    const filename = `${image.seriesMetadata.modality}_${image.dicomUid}_${index}.dcm`;
                    zip.file(filename, blob);
                });
            }

            for (const {
                structureSet,
                selectedRois,
            } of selectedStructureSets) {
                const blob = structureSetToBlob(
                    structureSet,
                    selectedRois,
                    exportToMonaco
                );
                if (blob) {
                    zip.file(`RS_${structureSet.structureSetLabel}.dcm`, blob);
                }

                if (exportToMonaco) {
                    const monacoBlob = monacoStructureSetToBlob(structureSet);
                    if (monacoBlob) {
                        zip.file(
                            `RS_${structureSet.structureSetLabel}.dir`,
                            monacoBlob
                        );
                    }
                }
            }

            for (const dose of selectedDoses) {
                const { doseBlob, planBlob } = getDoseBlobs(dose);
                if (doseBlob) {
                    zip.file(
                        `RTDOSE_${dose.plan?.label ?? dose.dicomUid}.dcm`,
                        doseBlob
                    );
                }
                if (planBlob) {
                    zip.file(
                        `RTPLAN_${dose.plan?.label ?? dose.dicomUid}.dcm`,
                        planBlob
                    );
                }
            }

            // Generate and download the ZIP file
            const zipBlob = await zip.generateAsync({
                type: 'blob',
                compression: 'DEFLATE',
            });
            saveBlobToFile(zipBlob, `${filename}.zip`);
        } catch (error) {
            handleError(error, 'Failed to download DICOM files');
        } finally {
            setExporting(false);
        }
    };

    /**
     * Exports multiple selected `StructureSet`, `DoseMatrix`, and `Image3D` in a single batch upload.
     *
     * This function processes multiple objects,
     * extracts the corresponding DICOM files, and uploads them all in separate API requests.
     *
     * @param {SelectedStructureSetsWithStructures[]} selectedStructureSets -
     *        An array of objects containing a StructureSet and its selected ROIs.
     * @param {DoseMatrix[]} selectedDoses -
     *        An array of DoseMatrix objects representing dose distributions to be exported.
     * @param {Image3D[]} selectedImages -
     *        An array of Image3D objects representing images to be exported.
     * @returns {Promise<void>} A promise that resolves when the upload process is completed.
     */
    const handleExportBatch = async (
        selectedImages: Image3D[] = [],
        selectedStructureSets: SelectedStructureSetsWithStructures[],
        selectedDoses: DoseMatrix[]
    ) => {
        try {
            setExporting(true);

            for (const image of selectedImages) {
                const blobs = Array.from(image.getSlicesAsDicomBlobs());
                for (let i = 0; i < blobs.length; i++) {
                    const blob = blobs[i];
                    await uploadDicomBlob(
                        blob,
                        `${image.seriesMetadata.modality}_${image.dicomUid}_${i}.dcm`
                    );
                }
            }

            for (const structureSetWithStructures of selectedStructureSets) {
                const blob = structureSetToBlob(
                    structureSetWithStructures.structureSet,
                    structureSetWithStructures.selectedRois
                );
                if (blob) {
                    await uploadDicomBlob(
                        blob,
                        `RS_${structureSetWithStructures.structureSet.structureSetLabel}.dcm`
                    );
                }
            }

            for (const dose of selectedDoses) {
                const { doseBlob, planBlob } = getDoseBlobs(dose);
                if (planBlob) {
                    await uploadDicomBlob(
                        planBlob,
                        `RTPLAN_${dose.plan?.label ?? dose.dicomUid}.dcm`
                    );
                }
                if (doseBlob) {
                    await uploadDicomBlob(
                        doseBlob,
                        `RTDOSE_${dose.plan?.label ?? dose.dicomUid}.dcm`
                    );
                }
            }

            selectedImages.forEach((image) => {
                image.setExported();
            });

            selectedStructureSets.forEach(({ structureSet }) => {
                structureSet.setExported();
            });

            selectedDoses.forEach((dose) => {
                dose.setExported();
                dose.plan?.setExported();
            });

            enqueueSnackbar('All selected DICOM files uploaded successfully', {
                variant: 'success',
            });
        } catch (error) {
            console.error('Error during batch export:', error);
            enqueueSnackbar('Failed to upload batch DICOM files', {
                variant: 'error',
            });
        } finally {
            setExporting(false);
        }
    };
    return {
        exporting,
        handleDownloadStructureSet,
        handleExportImage,
        handleExportStructureSet,
        handleExportDose,
        handleDownloadDose,
        handleDownloadBatch,
        handleExportBatch,
    };
};
